import React, { useEffect, useState, useRef } from "react";
import { useParams, useOutletContext, useNavigate } from "react-router-dom";
import { 
  Send, MapPin, CheckCircle, Truck, Calendar, 
  Navigation, ShieldCheck, Loader2, PlayCircle, StopCircle,
  Radio, RadioTower, MessageSquareOff, MessageSquare, AlertOctagon, X, SatelliteDish
} from "lucide-react";

import RouteRow from "../Includes/RouteRow";

// ── Read-receipt ticks ──────────────────────────────────────────────────────
// Same visual language as the dispatcher app: single grey check while a
// message is still an optimistic/pending send, double grey once the server
// has confirmed it, double blue once the dispatcher has actually opened the
// chat and read it (fed by the "read_receipt" WebSocket event below).
function ReadTicks({ pending, read }) {
  if (pending) {
    return (
      <svg className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" viewBox="0 0 16 16" fill="none">
        <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  return (
    <svg className={`w-4 h-3.5 flex-shrink-0 ${read ? "text-sky-300" : "text-emerald-200"}`} viewBox="0 0 20 16" fill="none">
      <path d="M1 8.5l3.5 3.5L11 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7.5 8.5l3.5 3.5L18 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ── Emoji picker ─────────────────────────────────────────────────────────────
// Regressed in a previous upload — re-added. Same component as the
// dispatcher app's Includes/RightPanel.jsx, kept as a duplicate rather than
// a shared import since these are two separate projects/deployments.
// WhatsApp-style: category tabs (one representative emoji as the tab icon)
// switch which grid is shown, ~96 emoji reachable without one giant wall.
const EMOJI_CATEGORIES = [
  { key: "smileys", tabIcon: "😀", label: "Smileys", items: [
    "😀","😁","😂","🤣","😊","😇","🙂","🙃","😉","😍","😘","😋","😜","🤪","😎","🥳",
    "😢","😭","😤","😠","😡","🤬","😱","😨","😴","🥱","🤔","🤗","🙄","😳","🥵","🥶",
  ]},
  { key: "gestures", tabIcon: "👍", label: "Gestures", items: [
    "👍","👎","👋","🙏","👏","🤝","💪","✌️","🤞","👌","🤙","☝️","👉","👈","🖐️","✋",
    "❤️","🧡","💛","💚","💙","💜","🖤","🤍","💯","🔥","✨","⭐","💥","💤","👀","🎉",
  ]},
  { key: "logistics", tabIcon: "🚚", label: "Logistics", items: [
    "🚚","🚛","🚗","🚨","📍","🗺️","🧭","⏰","⏱️","🛑","🏁","⛽","🔧","🔩","📦","🚧",
    "🌧️","☀️","⛅","❄️","🌙","🌡️","🌪️","🌫️","📞","📱","✅","❌","⚠️","❗","❓","🆗",
  ]},
];

function EmojiPicker({ onSelect, onClose }) {
  const ref = useRef(null);
  const [activeCategory, setActiveCategory] = useState(EMOJI_CATEGORIES[0].key);
  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [onClose]);

  const active = EMOJI_CATEGORIES.find(c => c.key === activeCategory) || EMOJI_CATEGORIES[0];

  return (
    <div ref={ref} className="absolute bottom-full left-0 mb-2 w-72 bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden z-20">
      <div className="flex items-center gap-0.5 px-2 pt-2 pb-1 border-b border-slate-100 bg-slate-50">
        {EMOJI_CATEGORIES.map(cat => (
          <button
            key={cat.key}
            type="button"
            title={cat.label}
            onClick={() => setActiveCategory(cat.key)}
            className={`flex-1 text-lg leading-none py-1.5 rounded-lg transition-colors ${
              activeCategory === cat.key ? "bg-white shadow-sm ring-1 ring-slate-200" : "opacity-50 hover:opacity-90"
            }`}
          >
            {cat.tabIcon}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-8 gap-0.5 p-2 max-h-48 overflow-y-auto custom-scrollbar">
        {active.items.map((emoji, i) => (
          <button
            key={`${emoji}-${i}`}
            type="button"
            onClick={() => onSelect(emoji)}
            className="text-lg leading-none py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}

// Quick-reply chips — the same status shortcuts the dispatcher app already
// has (Arrived/Delayed/Rest Stop/Police Check), missing from the driver
// side until now despite being an obvious two-way need.
const QUICK_REPLIES = ["Arrived", "Delayed", "Rest Stop", "Police Check"];

// Shared chat body — used by BOTH the desktop right column and the mobile
// fixed-position Chat tab (see JobConsolePage below). Extracted so the two
// views can never drift out of sync with each other the way a copy-pasted
// duplicate would.
function ChatPanelBody({
  assignment, messages, chatInput, handleChatInputChange, sendMessage,
  chatConnected, dispatcherTyping, chatEndRef, emojiOpen, setEmojiOpen,
  isCompleted, isStarted, user,
}) {
  return (
    <>
      {/* Chat Header */}
      <div className="bg-[#1a2744] text-white p-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center border border-white/20">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-bold text-sm leading-tight">Dispatcher Chat</h2>
            <p className="text-xs text-blue-200">
              {assignment.dispatcher ? assignment.dispatcher.full_name : "Waiting for dispatcher to join"}
            </p>
          </div>
        </div>
      </div>

      {/* Live connection status — a real WebSocket, not polling; this
          only shows if it's briefly reconnecting after a network blip. */}
      {assignment.is_chat_active && !chatConnected && (
        <div className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-50 border-b border-amber-100 flex-shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
          <span className="text-[11px] text-amber-700 font-medium">Reconnecting live chat…</span>
        </div>
      )}

      {/* Chat History */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4 bg-slate-50/50 custom-scrollbar min-h-0">
        {!assignment.is_chat_active && (
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center mb-3">
              <Navigation className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-slate-500 font-bold">
              {isStarted ? "Dispatch Communication Paused" : "Chat is currently disabled"}
            </p>
            <p className="text-sm text-slate-400 mt-1">
              {isStarted
                ? "Resume communication from the panel on the left to continue messaging."
                : "Start the load to begin communicating with dispatch."}
            </p>
          </div>
        )}

        {messages.map((msg, idx) => {
          const isSystem = msg.message_type === "system" || msg.sender_name === "System";
          const senderId = typeof msg.sender === "object" ? msg.sender?.id : msg.sender;
          const isMe = senderId != null && user?.id != null && String(senderId) === String(user.id);

          if (isSystem) {
            return (
              <div key={msg.id || idx} className="flex w-full justify-center my-2">
                <span className="text-[11px] font-bold text-slate-500 bg-slate-200/60 px-3 py-1 rounded-full text-center max-w-[80%] break-words">
                  {msg.content}
                </span>
              </div>
            );
          }

          return (
            // `w-full` here is the actual fix for the cut-off/overflowing
            // bubbles: without it this row (a block-level flex container)
            // can end up sized to its own content on some mobile browsers
            // instead of the full chat width, which makes the `max-w-[75%]`
            // percentage below resolve against the wrong (too-narrow, or
            // in edge cases wider-than-viewport) base — the same class of
            // bug as the classic "flex child overflows because nothing
            // gave it a width to shrink against" gotcha. WhatsApp-style
            // sizing: bubble hugs its text up to 75% of the chat width,
            // wraps well before that, never gets clipped by the screen edge.
            <div key={msg.id || idx} className={`flex flex-col w-full ${isMe ? "items-end" : "items-start"} ${msg.pending ? "opacity-60" : ""}`}>
              <div className={`max-w-[75%] min-w-0 p-3 rounded-2xl shadow-sm ${
                isMe 
                  ? "bg-emerald-600 text-white rounded-tr-sm" 
                  : "bg-white border border-slate-200 text-slate-800 rounded-tl-sm"
              }`}>
                {!isMe && (
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">
                    {msg.sender_name}
                  </p>
                )}
                <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{msg.content}</p>
              </div>
              <span className="text-[10px] text-slate-400 mt-1 px-1 font-semibold flex items-center gap-1">
                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {isMe && <ReadTicks pending={!!msg.pending} read={!!msg.is_read} />}
              </span>
            </div>
          );
        })}
        {dispatcherTyping && assignment.is_chat_active && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 px-3 py-2.5 rounded-2xl rounded-tl-sm shadow-sm flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Quick replies */}
      {assignment.is_chat_active && !isCompleted && (
        <div className="flex items-center gap-1.5 px-3 pt-2 overflow-x-auto flex-shrink-0">
          {QUICK_REPLIES.map(q => (
            <button key={q} onClick={() => sendMessage(null, q)}
              className="flex-shrink-0 text-xs px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-emerald-400 hover:text-emerald-600 whitespace-nowrap transition-colors">
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Chat Input */}
      <form onSubmit={sendMessage} className="relative p-4 bg-white border-t border-slate-100 flex gap-2 shrink-0">
        {emojiOpen && (
          <EmojiPicker
            onSelect={(emoji) => handleChatInputChange((chatInput || "") + emoji)}
            onClose={() => setEmojiOpen(false)}
          />
        )}
        <button
          type="button"
          onClick={() => setEmojiOpen(v => !v)}
          disabled={!assignment.is_chat_active || isCompleted}
          title="Emoji"
          className="text-xl leading-none text-slate-400 hover:text-slate-600 disabled:opacity-40 flex-shrink-0 px-1"
        >
          🙂
        </button>
        <input
          disabled={!assignment.is_chat_active || isCompleted}
          type="text"
          value={chatInput}
          onChange={(e) => handleChatInputChange(e.target.value)}
          placeholder={assignment.is_chat_active ? "Type a message..." : "Start load to chat"}
          className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <button 
          type="submit"
          disabled={!assignment.is_chat_active || !chatInput.trim() || isCompleted}
          className="bg-[#1a2744] text-white p-3.5 rounded-xl hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          <Send className="w-5 h-5" />
        </button>
      </form>
    </>
  );
}

export default function JobConsolePage() {
  const { id } = useParams(); // LoadAssignment UUID
  const navigate = useNavigate();
  
  // Grab user directly from the Layout Context
  const { user } = useOutletContext() || {};

  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [chatConnected, setChatConnected] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  // Mobile Detail/Chat tab switcher — on mobile the two columns used to
  // stack vertically (job details, tracking controls, route info, THEN
  // chat), so reaching chat meant scrolling past everything else first.
  // This mirrors the dispatcher app's own Detail/Chat tab pattern so
  // chat gets its own dedicated, fully-visible view on mobile instead of
  // fighting the rest of the page for scroll space. Desktop is unaffected
  // — both columns stay side-by-side there (lg: breakpoint below).
  const [mobileTab, setMobileTab] = useState("detail");
  const [hasUnseenMessage, setHasUnseenMessage] = useState(false);
  const mobileTabRef = useRef("detail");
  useEffect(() => { mobileTabRef.current = mobileTab; }, [mobileTab]);
  const [dispatcherTyping, setDispatcherTyping] = useState(false);

  // Premium controls
  const [togglingTracking, setTogglingTracking] = useState(false);
  const [togglingComms, setTogglingComms] = useState(false);
  const [gpsAccuracyWarning, setGpsAccuracyWarning] = useState(null);
  // 'unknown' | 'prompt' | 'granted' | 'denied' | 'unsupported' | 'insecure'
  // Drives the "ask for permission" banner below — this is what was
  // missing for iOS: the old code called watchPosition() straight from a
  // useEffect on mount with no guaranteed user gesture in the call stack.
  // iOS Safari can and does silently swallow that (no prompt, no error,
  // the success callback just never fires) unless the very first
  // geolocation call happens synchronously inside a real click handler.
  // requestLocationPermission() below is written to always be called that
  // way — from handleStartLoad, handleToggleTracking, and this banner's
  // own button — so iOS reliably shows its system prompt (matches the
  // native "wants to use your device's location" sheet already working
  // correctly on Android in the same app).
  const [locationPermission, setLocationPermission] = useState("unknown");
  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isStandalonePWA = typeof navigator !== "undefined" && (window.navigator.standalone === true || window.matchMedia?.("(display-mode: standalone)")?.matches);
  const [sosOpen, setSosOpen] = useState(false);
  const [sosNote, setSosNote] = useState("");
  const [sosSending, setSosSending] = useState(false);
  const [sosSent, setSosSent] = useState(false);

  // References
  const chatEndRef = useRef(null);
  const chatWsRef = useRef(null); // Holds the WebSocket connection
  const reconnectTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const myTypingSentRef = useRef(false);
  const myTypingStopTimeoutRef = useRef(null);

  // 1. Fetch Job Details
  // BUG FIX: the "Failed to fetch" errors appearing in the console while
  // on a totally different page (Invoices) came from here — this used a
  // `cancelled` flag to stop STATE updates after unmount, but never
  // actually stopped the underlying network request. If that fetch failed
  // after the user had already navigated away, the catch block's
  // console.error still fired regardless of the flag, showing up as a
  // confusing phantom error on whatever page they'd moved to. AbortController
  // actually cancels the in-flight request on unmount, and an aborted
  // fetch is expected, not logged as a real error.
  useEffect(() => {
    const controller = new AbortController();
    const fetchAssignment = async () => {
      try {
        const token = localStorage.getItem("token") || localStorage.getItem("user_token") || localStorage.getItem("access_token");
        if (!token) return;

        const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
        const res = await fetch(`${apiUrl}/api/loads/assignments/mine/`, {
          headers: { "Authorization": `Bearer ${token}` },
          signal: controller.signal,
        });
        if (res.ok) {
          const data = await res.json();
          const currentJob = data.find(a => a.id === id);
          if (currentJob) setAssignment(currentJob);
        }
      } catch (err) {
        if (err.name !== "AbortError") console.error(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchAssignment();
    return () => controller.abort();
  }, [id]);

  // 2. WebSocket Setup & Initial Chat History
  // BUG FIX — this used to have no reconnect logic at all: if the socket
  // dropped for any reason (network blip, server restart), chat just
  // silently stopped working with no retry until a manual page refresh.
  // It also never sent "mark_read" or listened for "read_receipt"/"typing"
  // — the backend (loads/consumers.py::LoadChatConsumer) already fully
  // supports all three, they just weren't wired up here, which is why read
  // receipts never showed up in real time on the dispatcher's side no
  // matter what got fixed there. And the cleanup closed the socket
  // unconditionally regardless of readyState — closing a socket that's
  // still CONNECTING is ambiguous enough that the server can finish
  // registering it before the close lands, leaving a zombie connection
  // (visible in DevTools' Network -> WS panel as more than one open
  // connection to the same room). All three are fixed below, mirroring the
  // same pattern used in the dispatcher app.
  useEffect(() => {
    if (!assignment?.id) return;
    const token = localStorage.getItem("token") || localStorage.getItem("user_token") || localStorage.getItem("access_token");
    const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
    let cancelled = false;
    let attempt = 0;

    // Fetch History ONCE
    if (assignment.is_chat_active) {
      fetch(`${apiUrl}/api/loads/assignments/${id}/chat/history/`, {
        headers: { "Authorization": `Bearer ${token}` }
      })
      .then(res => res.ok ? res.json() : [])
      .then(msgs => { if (!cancelled) setMessages(msgs); })
      .catch(console.error);
    }

    const wsProtocol = window.location.protocol === "https:" ? "wss://" : "ws://";
    const wsDomain = process.env.REACT_APP_WS_URL || "127.0.0.1:8000"; 
    const wsUrl = `${wsProtocol}${wsDomain}/ws/chat/load/${id}/?token=${token}`;

    const connect = () => {
      if (cancelled) return;
      const ws = new WebSocket(wsUrl);
      chatWsRef.current = ws;

      ws.onopen = () => {
        // Never force-close a CONNECTING socket from cleanup (see return()
        // below) — close it here instead, the instant the handshake
        // actually completes, which is the unambiguous point to do it from.
        if (cancelled) { ws.close(); return; }
        attempt = 0;
        setChatConnected(true);
        if (assignment.is_chat_active) {
          ws.send(JSON.stringify({ type: "mark_read" }));
        }
      };

      ws.onmessage = (event) => {
        if (cancelled) return;
        const data = JSON.parse(event.data);

        if (data.type === "chat_message") {
          setMessages(prev => {
            // Reconcile against our own optimistic echo instead of
            // appending a duplicate — match on client_id if the server
            // echoes it back, else the newest pending message with the
            // same text.
            const byClientId = data.client_id && prev.findIndex(m => m.id === data.client_id);
            const byHeuristic = prev.findIndex(m => m.pending && m.content === data.content);
            const idx = (byClientId != null && byClientId > -1) ? byClientId : byHeuristic;
            if (idx > -1) { const next = [...prev]; next[idx] = data; return next; }
            return [...prev, data];
          });
          const senderId = typeof data.sender === "object" ? data.sender?.id : data.sender;
          if (String(senderId) !== String(user?.id) && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "mark_read" }));
            // Flag the Chat tab if this message arrived while the driver
            // is looking at Detail on mobile — otherwise a new message
            // could arrive with zero visible indication until they
            // happen to switch tabs.
            if (mobileTabRef.current !== "chat") setHasUnseenMessage(true);
          }
        } else if (data.type === "read_receipt") {
          if (String(data.reader_id) !== String(user?.id)) {
            setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
          }
        } else if (data.type === "typing") {
          setDispatcherTyping(!!data.is_typing);
          clearTimeout(typingTimeoutRef.current);
          if (data.is_typing) typingTimeoutRef.current = setTimeout(() => setDispatcherTyping(false), 6000);
        } else if (data.type === "location_requested") {
          // Dispatcher asked for an immediate fix — bypass the normal
          // adaptive throttle (see the watchPosition effect further down)
          // entirely with a one-off getCurrentPosition() call, sent the
          // instant it resolves instead of waiting for the next scheduled
          // ping. Note: this fires from a WebSocket message, not a click,
          // so on iOS it can only succeed if permission was already
          // granted earlier (e.g. via Start Load) — it can't itself pop a
          // fresh permission prompt. If it fails on "denied", we still
          // surface that in the UI so the driver isn't left guessing why
          // the dispatcher says nothing came through.
          setLocationRequestPending(true);
          if (!window.isSecureContext) { setLocationPermission("insecure"); setLocationRequestPending(false); }
          else if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                setLocationPermission("granted");
                const { latitude, longitude, heading, speed, accuracy } = position.coords;
                if (chatWsRef.current?.readyState === WebSocket.OPEN) {
                  chatWsRef.current.send(JSON.stringify({
                    type: "gps_update", lat: latitude, lng: longitude, heading,
                    speed: speed != null ? speed * 2.23694 : null, accuracy,
                  }));
                }
                setLocationRequestPending(false);
              },
              (error) => {
                if (error.code === 1) setLocationPermission("denied");
                setLocationRequestPending(false);
              },
              { enableHighAccuracy: true, timeout: 15000 }
            );
          } else {
            setLocationPermission("unsupported");
            setLocationRequestPending(false);
          }
        }
      };

      ws.onclose = () => {
        setChatConnected(false);
        if (cancelled) return; // this effect run was superseded — don't reconnect on its behalf
        const timeout = Math.min(1000 * (2 ** attempt), 30000);
        attempt++;
        reconnectTimeoutRef.current = setTimeout(connect, timeout);
      };
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimeoutRef.current);
      setChatConnected(false);
      setDispatcherTyping(false);
      const ws = chatWsRef.current;
      if (ws) {
        ws.onclose = null;
        if (ws.readyState === WebSocket.OPEN) ws.close();
        // Still CONNECTING? Left alone — onopen above closes it once the
        // handshake completes instead of racing a close mid-handshake.
      }
    };
  }, [assignment?.id, id, assignment?.is_chat_active, user?.id]);

  // Debounced typing sender — tells the dispatcher "driver is typing…" and
  // clears it 3s after input stops or on send.
  const handleChatInputChange = (value) => {
    setChatInput(value);
    if (!chatWsRef.current || chatWsRef.current.readyState !== WebSocket.OPEN) return;
    if (!myTypingSentRef.current) {
      chatWsRef.current.send(JSON.stringify({ type: "typing", is_typing: true }));
      myTypingSentRef.current = true;
    }
    clearTimeout(myTypingStopTimeoutRef.current);
    myTypingStopTimeoutRef.current = setTimeout(() => {
      if (chatWsRef.current?.readyState === WebSocket.OPEN) {
        chatWsRef.current.send(JSON.stringify({ type: "typing", is_typing: false }));
      }
      myTypingSentRef.current = false;
    }, 3000);
  };

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, dispatcherTyping]);

  // iOS keyboard fix, part 2: `scrollIntoView` above fires the instant a
  // message is added, but on iOS the on-screen keyboard animates open over
  // ~250-300ms — if the scroll happens before that animation finishes, it
  // scrolls to a position that's correct for the pre-keyboard layout and
  // then the keyboard covers it anyway once it finishes opening. The
  // visualViewport API reports the *actual* visible area and fires
  // "resize" as the keyboard animates, so re-scrolling on that event
  // keeps the latest message in view once the keyboard has actually
  // settled, on the browsers that support it (iOS Safari 13+, Chrome).
  useEffect(() => {
    if (!window.visualViewport) return;
    const onViewportResize = () => {
      chatEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
    };
    window.visualViewport.addEventListener("resize", onViewportResize);
    return () => window.visualViewport.removeEventListener("resize", onViewportResize);
  }, []);

  // 3. Background GPS Tracking (Pushed via WebSocket)
  //
  // ── Honest note on "GPS should keep working when minimized" ────────────
  // A plain browser tab (not installed as an app) cannot reliably keep
  // sending location once truly backgrounded — that's a platform
  // limitation, not a bug here:
  //   - iOS Safari suspends JS timers, geolocation, and often the socket
  //     itself within seconds of losing focus or the screen locking. No
  //     web API overrides this today.
  //   - Android Chrome is more lenient but still throttles and eventually
  //     suspends backgrounded tabs.
  //   - Real continuous background tracking needs a native app (or a PWA
  //     installed to the home screen with background-location
  //     entitlements) using a native background-location API — a
  //     different build, not a setting in this React app. See
  //     BACKGROUND_GPS.md for the full explanation.
  // What IS added below, the honest ceiling for a browser tab: a Screen
  // Wake Lock (keeps the screen from sleeping while tracking, on browsers
  // that support it — not iOS Safari) and a visible warning when the tab
  // is backgrounded, instead of silently going stale with no explanation.
  const [isBackgrounded, setIsBackgrounded] = useState(false);
  const [locationRequestPending, setLocationRequestPending] = useState(false);
  const wakeLockRef = useRef(null);

  // Best-effort *read* of current permission state — purely informational,
  // never itself a prompt. Chrome/Android supports the Permissions API for
  // geolocation fully; Safari (iOS and macOS) has historically not exposed
  // it for geolocation at all, so this silently no-ops there and the app
  // just waits to learn the real state from the first actual
  // getCurrentPosition()/watchPosition() call instead — see
  // requestLocationPermission() below, which is what actually asks.
  useEffect(() => {
    if (!window.isSecureContext) { setLocationPermission("insecure"); return; }
    if (!("geolocation" in navigator)) { setLocationPermission("unsupported"); return; }
    if (!navigator.permissions?.query) return; // e.g. Safari — stay "unknown" until a real request
    let cancelled = false;
    navigator.permissions.query({ name: "geolocation" })
      .then((status) => {
        if (cancelled) return;
        setLocationPermission(status.state); // 'granted' | 'denied' | 'prompt'
        status.onchange = () => setLocationPermission(status.state);
      })
      .catch(() => {}); // some browsers reject unknown permission names — ignore, not fatal
    return () => { cancelled = true; };
  }, []);

  // The one function that actually triggers the OS/browser permission
  // dialog. MUST be called synchronously from inside a real click handler
  // (no `await` before it) — that's what makes iOS Safari show its prompt
  // reliably instead of silently doing nothing. Wired into:
  //   - handleStartLoad (first thing that needs location)
  //   - handleToggleTracking (resuming after a pause)
  //   - the "Enable Location Access" banner button below, for a driver who
  //     dismissed/missed the prompt the first time
  const requestLocationPermission = () => {
    if (!window.isSecureContext) { setLocationPermission("insecure"); return; }
    if (!("geolocation" in navigator)) { setLocationPermission("unsupported"); return; }
    navigator.geolocation.getCurrentPosition(
      () => setLocationPermission("granted"),
      (error) => setLocationPermission(error.code === 1 ? "denied" : "prompt"),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  useEffect(() => {
    if (!assignment?.is_tracking_active) {
      wakeLockRef.current?.release?.().catch(() => {});
      wakeLockRef.current = null;
      return;
    }
    let cancelled = false;
    const requestLock = async () => {
      if (!("wakeLock" in navigator)) return;
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (cancelled) { lock.release().catch(() => {}); return; }
        wakeLockRef.current = lock;
      } catch { /* not supported / denied — best-effort only */ }
    };
    requestLock();
    const onVisibility = () => {
      const hidden = document.visibilityState === "hidden";
      setIsBackgrounded(hidden);
      if (!hidden) requestLock(); // wake locks release automatically on hide — reacquire on return
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      wakeLockRef.current?.release?.().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [assignment?.is_tracking_active]);
  useEffect(() => {
    let watchId;
    let lastPingTime = 0;

    // Adaptive ping interval — moving vs. idle, matching how professional
    // fleet trackers balance battery life against accuracy: no reason to
    // ping every 5s while parked at a rest stop for 20 minutes.
    const MOVING_INTERVAL_MS = 7000;   // ~5-10s while actually driving
    const IDLE_INTERVAL_MS = 45000;    // ~30-60s while stationary
    const MOVING_SPEED_THRESHOLD_MS = 1.0; // ~2.2 mph — above this counts as "moving"; below is GPS jitter from being parked, not real movement

    // Don't even attempt watchPosition once we know it's hopeless — a
    // denied/unsupported/insecure state will just error immediately and
    // repeatedly, doing nothing but draining battery. 'unknown' and
    // 'prompt' are both still worth trying: 'unknown' covers Safari (no
    // Permissions API to read state from ahead of time) and 'prompt'
    // covers a driver returning to a tab where the OS dialog just hasn't
    // been answered yet.
    const blocked = locationPermission === "denied" || locationPermission === "insecure" || locationPermission === "unsupported";

    if (assignment?.is_tracking_active && !blocked) {
      if ("geolocation" in navigator) {
        watchId = navigator.geolocation.watchPosition(
          async (position) => {
            if (locationPermission !== "granted") setLocationPermission("granted");
            const now = Date.now();
            const isMoving = (position.coords.speed || 0) > MOVING_SPEED_THRESHOLD_MS;
            const currentInterval = isMoving ? MOVING_INTERVAL_MS : IDLE_INTERVAL_MS;
            if (now - lastPingTime < currentInterval) return;
            
            lastPingTime = now;
            const { latitude, longitude, heading, speed, accuracy } = position.coords;

            // BUG FIX — this was the "wrong mileage/speed on the dispatcher
            // dashboard" bug. `position.coords.speed` from the browser
            // Geolocation API is in METERS PER SECOND, always — it was being
            // sent to the backend raw and displayed as "mph" downstream
            // (see mapAssignment in the dispatcher app), so a truck actually
            // doing 60 mph (~26.8 m/s) showed up as "27 mph". Converting at
            // the source, once, here — nothing downstream needs to guess
            // about units anymore.
            const speedMph = speed != null ? speed * 2.23694 : null;

            // Flag poor accuracy logic locally
            if (accuracy > 150) {
                setGpsAccuracyWarning({ accuracy: Math.round(accuracy) });
            } else {
                setGpsAccuracyWarning(null);
            }

            // A. Attempt INSTANT Redis Push via WebSocket
            if (chatWsRef.current && chatWsRef.current.readyState === WebSocket.OPEN) {
                chatWsRef.current.send(JSON.stringify({
                    type: "gps_update",
                    lat: latitude,
                    lng: longitude,
                    heading: heading,
                    speed: speedMph,
                    accuracy: accuracy
                }));
            } else {
                // B. Fallback to HTTP POST if WebSocket is reconnecting
                try {
                  const token = localStorage.getItem("token") || localStorage.getItem("user_token") || localStorage.getItem("access_token");
                  const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
                  fetch(`${apiUrl}/api/loads/assignments/${id}/gps/ping/`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                    body: JSON.stringify({ lat: latitude, lng: longitude, heading, speed: speedMph, accuracy })
                  }).catch(() => {});
                } catch (error) {
                  console.error("GPS HTTP Fallback Error:", error);
                }
            }
          },
          (error) => {
            // code 1 = PERMISSION_DENIED — this is the case that was
            // silently doing nothing on iOS before: no ping ever sent, no
            // visible explanation. Now it flips the banner on immediately.
            if (error.code === 1) { setLocationPermission("denied"); return; }
            if (error.code !== 3) console.warn("GPS Warning:", error.message);
          },
          { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 }
        );
      }
    }
    return () => {
      if (watchId) navigator.geolocation.clearWatch(watchId);
    };
  }, [assignment?.is_tracking_active, id, locationPermission]);

  // Actions
  const handleStartLoad = async () => {
    // Fire this FIRST and synchronously, before any `await` — this is
    // what actually fixes GPS not working on iOS. The button click is a
    // real user gesture; asking for location permission as literally the
    // first thing that happens inside that gesture is what makes iOS
    // Safari show its system prompt reliably (see Image 3 — that dialog
    // was already working correctly on Android; this line is what makes
    // iOS show the equivalent one instead of silently never asking).
    requestLocationPermission();
    setActionLoading(true);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("user_token") || localStorage.getItem("access_token");
      const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
      await fetch(`${apiUrl}/api/loads/assignments/${id}/start/`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      // Simple reload mechanism for states
      window.location.reload(); 
    } catch (err) {
      alert("Failed to start load.");
      setActionLoading(false);
    }
  };

  const [showCompletionPrompt, setShowCompletionPrompt] = useState(false);

  const handleCompleteLoad = async () => {
    if (!window.confirm("Are you sure you want to mark this load as completed?")) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("user_token") || localStorage.getItem("access_token");
      const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
      const res = await fetch(`${apiUrl}/api/loads/assignments/${id}/complete/`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        // Used to be a hard window.location.reload() here — replaced with
        // a real completion prompt (below) reminding the driver to
        // generate + send their invoice while the job is fresh, instead
        // of silently reloading and hoping they remember to do it later
        // from the Invoices page on their own.
        setShowCompletionPrompt(true);
      } else {
        alert("Failed to complete load.");
      }
    } catch (err) {
      alert("Failed to complete load.");
    } finally {
      setActionLoading(false);
    } 
  };

  const handleToggleTracking = async () => {
    // Same reasoning as handleStartLoad — ask again synchronously inside
    // this click when re-enabling tracking after a pause, in case
    // permission state changed (e.g. the driver denied it the first time
    // and has since fixed it in iOS Settings).
    if (!assignment.is_tracking_active) requestLocationPermission();
    setTogglingTracking(true);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("user_token") || localStorage.getItem("access_token");
      const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
      const res = await fetch(`${apiUrl}/api/loads/assignments/${id}/gps/toggle/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ enabled: !assignment.is_tracking_active }),
      });
      if (res.ok) setAssignment(await res.json());
    } catch (err) {
      alert("Couldn't update live tracking right now.");
    } finally {
      setTogglingTracking(false);
    }
  };

  const handleToggleComms = async () => {
    setTogglingComms(true);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("user_token") || localStorage.getItem("access_token");
      const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
      const res = await fetch(`${apiUrl}/api/loads/assignments/${id}/chat/toggle/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ enabled: !assignment.is_chat_active }),
      });
      if (res.ok) setAssignment(await res.json());
    } catch (err) {
      alert("Couldn't update dispatch communication right now.");
    } finally {
      setTogglingComms(false);
    }
  };

  const handleSendSOS = async () => {
    setSosSending(true);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("user_token") || localStorage.getItem("access_token");
      const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
      const res = await fetch(`${apiUrl}/api/loads/assignments/${id}/sos/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ note: sosNote.trim() }),
      });
      if (res.ok) {
        setSosSent(true);
        setTimeout(() => { setSosOpen(false); setSosSent(false); setSosNote(""); }, 2500);
      } else {
        alert("Couldn't send the alert — try calling your dispatcher directly if this persists.");
      }
    } catch (err) {
      alert("Couldn't send the alert — try calling your dispatcher directly if this persists.");
    } finally {
      setSosSending(false);
    }
  };

  // SEND MESSAGE via WebSocket
  const sendMessage = async (e, overrideText) => {
    e?.preventDefault?.();
    const text = (overrideText ?? chatInput).trim();
    if (!text) return;
    setChatInput(""); 

    // Optimistic echo: show the message immediately instead of waiting on
    // the server round-trip. Marked `pending` (faded, single grey check)
    // until the real copy comes back over the socket and reconciles it.
    const tempId = `pending-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: tempId, content: text, sender: user?.id,
      sender_name: "You", created_at: new Date().toISOString(), pending: true,
    }]);

    // A. Push instantly via WebSocket
    if (chatWsRef.current && chatWsRef.current.readyState === WebSocket.OPEN) {
        chatWsRef.current.send(JSON.stringify({
            type: "message",
            message: text,
            client_id: tempId,
        }));
    } else {
        // B. Fallback to standard HTTP
        try {
          const token = localStorage.getItem("token") || localStorage.getItem("user_token") || localStorage.getItem("access_token");
          const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
          const res = await fetch(`${apiUrl}/api/loads/assignments/${id}/chat/send/`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify({ text })
          });
          if (res.ok) {
            // Re-fetch chat manually if forced to use fallback
            const msgsRes = await fetch(`${apiUrl}/api/loads/assignments/${id}/chat/history/`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            if (msgsRes.ok) setMessages(await msgsRes.json());
          }
        } catch (err) {
          console.error(err);
        }
    }
    if (chatWsRef.current?.readyState === WebSocket.OPEN && myTypingSentRef.current) {
      chatWsRef.current.send(JSON.stringify({ type: "typing", is_typing: false }));
      myTypingSentRef.current = false;
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center bg-white rounded-3xl border border-gray-200 min-h-[60vh]">
        <div className="flex flex-col items-center gap-2 text-slate-400">
          <Loader2 className="w-8 h-8 animate-spin text-[#1a2744]" />
          <span>Loading Job Console...</span>
        </div>
      </div>
    );
  }

  if (!assignment) return null;

  const loadData = assignment.load;
  const isStarted = assignment.status === "started" || assignment.status === "in_transit";
  const isCompleted = assignment.status === "completed";

  return (
    <div style={{ fontFamily: "Inter,system-ui,sans-serif" }} className="overflow-x-hidden w-full">

      {/* Mobile-only Detail/Chat tab switcher — desktop keeps both columns
          side-by-side (see lg:flex below) and doesn't need this at all.
          When Chat is active, this bar moves INSIDE the same `fixed`
          wrapper as the chat panel itself (below) rather than staying
          separately positioned in normal flow — two independently
          "guessed" fixed offsets is exactly how a gap/overlap bug like the
          one just fixed happens again. One wrapper, one measurement. */}
      {mobileTab === "detail" && (
        <div className="lg:hidden max-w-6xl mx-auto mb-3 flex bg-white rounded-xl border border-slate-200 p-1 shadow-sm">
          <button
            onClick={() => setMobileTab("detail")}
            className="flex-1 py-2 rounded-lg text-sm font-bold transition-colors bg-[#1a2744] text-white"
          >
            Detail
          </button>
          <button
            onClick={() => { setMobileTab("chat"); setHasUnseenMessage(false); }}
            className="relative flex-1 py-2 rounded-lg text-sm font-bold transition-colors text-slate-500"
          >
            Chat
            {hasUnseenMessage && (
              <span className="absolute top-1.5 right-[calc(50%-28px)] w-2 h-2 rounded-full bg-red-500" />
            )}
          </button>
        </div>
      )}

      {/* Mobile Chat: tab switcher + chat panel move TOGETHER as one fixed
          unit, precisely pinned between TopBar (h-14/56px) and BottomNav
          (bottom-16/64px reserved) — see the long comment further down on
          the chat panel itself for why `fixed` instead of height math.

          iOS fix: the offsets below now add `env(safe-area-inset-*)` on
          top of the plain 56px/64px. Without it, on any iPhone with a
          notch/Dynamic Island, this panel started exactly 56px from the
          true top of the *viewport* — which is a few dozen pixels ABOVE
          where the actual TopBar visually ends once its own safe-area
          padding is drawn — so this panel's own Detail/Chat mini tab bar
          rendered up under/behind the status bar and the real TopBar's
          "Detail" label bled through underneath it (exactly the overlap
          in the bug report screenshot). `overflow-x-hidden` + `w-full`
          additionally guarantee this full-bleed panel can never itself
          become a source of horizontal scroll/clipping on iOS Safari,
          which is what was pushing right-aligned chat bubbles past the
          right edge of the screen. */}
      {mobileTab === "chat" && (
        <div
          className="lg:hidden fixed inset-x-0 z-40 flex flex-col bg-white w-full overflow-x-hidden"
          style={{
            top: "calc(3.5rem + env(safe-area-inset-top, 0px))",
            bottom: "calc(4rem + env(safe-area-inset-bottom, 0px))",
          }}
        >
          <div className="flex-shrink-0 p-2 border-b border-slate-100 flex bg-white">
            <button
              onClick={() => setMobileTab("detail")}
              className="flex-1 py-2 rounded-lg text-sm font-bold transition-colors text-slate-500"
            >
              Detail
            </button>
            <button className="flex-1 py-2 rounded-lg text-sm font-bold transition-colors bg-[#1a2744] text-white mx-1">
              Chat
            </button>
          </div>
          <ChatPanelBody
            assignment={assignment} messages={messages} chatInput={chatInput}
            handleChatInputChange={handleChatInputChange} sendMessage={sendMessage}
            chatConnected={chatConnected} dispatcherTyping={dispatcherTyping}
            chatEndRef={chatEndRef} emojiOpen={emojiOpen} setEmojiOpen={setEmojiOpen}
            isCompleted={isCompleted} isStarted={isStarted} user={user}
          />
        </div>
      )}

      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-6 h-full lg:h-[calc(100vh-140px)]">
        
        {/* ─── LEFT COLUMN: JOB DETAILS & CONTROLS ─── */}
        {/* Route/Load Details card now comes FIRST and is visually the
            priority — it used to be squeezed into whatever `flex-1` space
            was left after the (quite tall, especially once a load is
            started) Controls card above it, forcing an extra internal
            scroll just to see the route/payout/notes you actually came
            here to check. Controls card also got a smaller, single-line
            header instead of a large title+status block — it's status
            information, not the main content. */}
        <div className={`w-full lg:w-1/3 flex-col gap-4 ${mobileTab === "detail" ? "flex" : "hidden lg:flex"}`}>

          {/* Route / Load Details — priority content, shown first */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex-1 overflow-y-auto custom-scrollbar">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Route Info</h3>
            <RouteRow from={loadData.from} to={loadData.to} />

            {loadData.meeting_point && (
              <div className="mt-4 p-3 bg-violet-50 rounded-xl border border-violet-100">
                <p className="text-[10px] font-black text-violet-800 uppercase tracking-widest mb-1 flex items-center gap-1"><MapPin className="w-3 h-3"/> Meeting Point</p>
                <p className="text-sm font-bold text-slate-900">{loadData.meeting_point}</p>
              </div>
            )}

            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mt-6 mb-3">Load Details</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                <p className="text-[10px] font-bold text-slate-400 uppercase">Payout</p>
                <p className="font-black text-emerald-600">{loadData.pay}</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                <p className="text-[10px] font-bold text-slate-400 uppercase">Dims</p>
                <p className="font-bold text-slate-800">{loadData.dims || "Standard"}</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 col-span-2">
                <p className="text-[10px] font-bold text-slate-400 uppercase">Commodity</p>
                <p className="font-bold text-slate-800">{loadData.commodity || "N/A"}</p>
              </div>
            </div>

            {loadData.notes && (
              <div className="mt-4 p-3 bg-teal-50 rounded-xl border border-teal-100">
                <p className="text-[10px] font-black text-teal-800 uppercase tracking-widest mb-1">Notes</p>
                <p className="text-sm font-medium text-slate-700">{loadData.notes}</p>
              </div>
            )}
          </div>

          {/* Controls Card — now a compact status/control strip, not the
              dominant block. Single-line title+status instead of a large
              header, since this is status info, not the main content. */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col gap-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-slate-800 truncate">
                {loadData.type || "Oversize Load"} <span className="text-slate-400 font-semibold">· {assignment.status}</span>
              </p>
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isStarted ? 'bg-emerald-500 animate-pulse' : isCompleted ? 'bg-slate-300' : 'bg-amber-400'}`} />
            </div>

            {!isStarted && !isCompleted && (
              <button 
                onClick={handleStartLoad}
                disabled={actionLoading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20 disabled:opacity-50"
              >
                {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <PlayCircle className="w-5 h-5" />}
                Start Load & Enable GPS
              </button>
            )}

            {isStarted && (
              <>
                <div className={`border rounded-xl p-3 flex items-center gap-2.5 transition-colors ${
                  assignment.is_tracking_active
                    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                    : "bg-amber-50 border-amber-200 text-amber-800"
                }`}>
                  <span className="relative flex h-3 w-3 shrink-0">
                    {assignment.is_tracking_active && (
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    )}
                    <span className={`relative inline-flex rounded-full h-3 w-3 ${assignment.is_tracking_active ? "bg-emerald-500" : "bg-amber-400"}`}></span>
                  </span>
                  <p className="text-xs font-bold">{assignment.is_tracking_active ? "Live Tracking Active" : "Live Tracking Paused"}</p>
                </div>

                {/* GPS permission banner — this is the actual fix for "live
                    tracking shows Active but no coordinates reach the
                    server on iOS". The card above reflects what the
                    *backend* thinks the state should be; this reflects
                    what the *browser* actually granted, and they can
                    disagree silently unless we check. Android generally
                    surfaces its own permission UI reliably on first
                    request (Image 3); iOS Safari is the one that needs
                    this explicit, always-visible fallback. */}
                {assignment.is_tracking_active && locationPermission === "denied" && (
                  <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 flex items-start gap-2.5 text-xs">
                    <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-bold">Location access is blocked — no GPS is reaching dispatch</p>
                      <p className="mt-0.5 opacity-90">
                        {isIOS
                          ? (isStandalonePWA
                              ? "Open iOS Settings → this app's name → Location, and set it to \u201cWhile Using the App.\u201d"
                              : "Open iOS Settings → Privacy & Security → Location Services → Safari Websites, and set it to \u201cAllow.\u201d Then come back and tap Retry.")
                          : "Tap the lock/site-info icon next to the address bar → Permissions → Location → Allow. Then tap Retry."}
                      </p>
                      <button
                        onClick={requestLocationPermission}
                        className="mt-2 text-[11px] font-bold bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                )}

                {assignment.is_tracking_active && (locationPermission === "unknown" || locationPermission === "prompt") && (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3 flex items-start gap-2.5 text-xs">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-bold">Location permission needed to start sending GPS</p>
                      <p className="mt-0.5 opacity-90">
                        Your browser hasn't confirmed location access yet — tap below to allow it now.
                      </p>
                      <button
                        onClick={requestLocationPermission}
                        className="mt-2 text-[11px] font-bold bg-amber-600 text-white px-3 py-1.5 rounded-lg hover:bg-amber-700 transition-colors"
                      >
                        Enable Location Access
                      </button>
                    </div>
                  </div>
                )}

                {assignment.is_tracking_active && locationPermission === "insecure" && (
                  <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 flex items-start gap-2.5 text-xs">
                    <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-bold">This page must be loaded over HTTPS for GPS to work</p>
                      <p className="mt-0.5 opacity-90">iOS and most browsers block location access entirely on non-secure (http://) pages.</p>
                    </div>
                  </div>
                )}

                {assignment.is_tracking_active && locationPermission === "unsupported" && (
                  <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 flex items-start gap-2.5 text-xs">
                    <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-bold">This browser doesn't support location sharing</p>
                      <p className="mt-0.5 opacity-90">Try opening this job link in Safari or Chrome instead.</p>
                    </div>
                  </div>
                )}

                {gpsAccuracyWarning && assignment.is_tracking_active && (
                  <div className="bg-orange-50 border border-orange-200 text-orange-800 rounded-xl p-3 flex items-start gap-2.5 text-xs">
                    <SatelliteDish className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-bold">Low-precision location (±{gpsAccuracyWarning.accuracy}m)</p>
                      <p className="mt-0.5 opacity-90">
                        This browser/device is estimating your position rather than using GPS
                        hardware. For accurate live tracking, open this job on your mobile device.
                      </p>
                    </div>
                  </div>
                )}

                {isBackgrounded && assignment.is_tracking_active && (
                  <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 flex items-start gap-2.5 text-xs">
                    <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-bold">This tab is in the background</p>
                      <p className="mt-0.5 opacity-90">
                        Keep this tab open and your screen on for continuous tracking — most phone
                        browsers pause location sharing once you switch apps or lock the screen.
                      </p>
                    </div>
                  </div>
                )}

                {locationRequestPending && (
                  <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-xl p-3 flex items-center gap-2.5 text-xs">
                    <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
                    <p className="font-bold">Dispatcher requested your location — sending now…</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    onClick={handleToggleTracking}
                    disabled={togglingTracking}
                    className={`flex flex-col items-center justify-center gap-1 py-3 rounded-xl border font-bold text-xs transition-colors disabled:opacity-50 ${
                      assignment.is_tracking_active
                        ? "bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    {togglingTracking ? <Loader2 className="w-4 h-4 animate-spin" /> : assignment.is_tracking_active ? <Radio className="w-4 h-4" /> : <RadioTower className="w-4 h-4" />}
                    {assignment.is_tracking_active ? "Pause Tracking" : "Resume Tracking"}
                  </button>
                  <button
                    onClick={handleToggleComms}
                    disabled={togglingComms}
                    className={`flex flex-col items-center justify-center gap-1 py-3 rounded-xl border font-bold text-xs transition-colors disabled:opacity-50 ${
                      assignment.is_chat_active
                        ? "bg-white border-blue-200 text-blue-700 hover:bg-blue-50"
                        : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    {togglingComms ? <Loader2 className="w-4 h-4 animate-spin" /> : assignment.is_chat_active ? <MessageSquare className="w-4 h-4" /> : <MessageSquareOff className="w-4 h-4" />}
                    {assignment.is_chat_active ? "Pause Communication" : "Resume Communication"}
                  </button>
                </div>

                <button
                  onClick={handleCompleteLoad}
                  disabled={actionLoading}
                  className="w-full bg-slate-900 hover:bg-black text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <StopCircle className="w-5 h-5" />}
                  Complete Job
                </button>

                <button
                  onClick={() => setSosOpen(true)}
                  className="w-full flex items-center justify-center gap-2 text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 font-bold py-2.5 rounded-xl transition-colors text-sm"
                >
                  <AlertOctagon className="w-4 h-4" /> Emergency Alert
                </button>
              </>
            )}

            {isCompleted && (
              <div className="bg-slate-100 border border-slate-200 text-slate-600 rounded-xl p-4 flex items-center gap-3 font-bold text-sm">
                <CheckCircle className="w-5 h-5 text-slate-400" /> Job Completed
              </div>
            )}
          </div>
        </div>

        {/* ─── RIGHT COLUMN: DISPATCHER CHAT (desktop only) ─── */}
        {/* Mobile chat now lives entirely in the dedicated `fixed` block
            near the top of this component (search "Mobile Chat: tab
            switcher + chat panel move TOGETHER") — this column is
            desktop-only now (`hidden lg:flex`, no mobile fixed variant),
            otherwise mobile would render two overlapping fixed chat
            panels at once when the Chat tab is active. */}
        <div className="hidden lg:flex lg:relative lg:flex-1 bg-white lg:rounded-2xl border border-slate-200 shadow-sm flex-col overflow-hidden lg:h-auto">
          <ChatPanelBody
            assignment={assignment} messages={messages} chatInput={chatInput}
            handleChatInputChange={handleChatInputChange} sendMessage={sendMessage}
            chatConnected={chatConnected} dispatcherTyping={dispatcherTyping}
            chatEndRef={chatEndRef} emojiOpen={emojiOpen} setEmojiOpen={setEmojiOpen}
            isCompleted={isCompleted} isStarted={isStarted} user={user}
          />
        </div>

      </div>
      {/* Emergency Alert Modal */}
      {sosOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && !sosSending && setSosOpen(false)}
        >
          <div className="bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-red-100 bg-red-50 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                  <AlertOctagon className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h2 className="text-base font-black text-red-900">Emergency Alert</h2>
                  <p className="text-xs text-red-700 mt-0.5">Notifies your dispatcher and company immediately.</p>
                </div>
              </div>
              {!sosSending && (
                <button onClick={() => setSosOpen(false)} className="w-7 h-7 rounded-lg hover:bg-red-100 flex items-center justify-center text-red-400 shrink-0">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="p-5">
              {sosSent ? (
                <div className="flex flex-col items-center text-center py-4 gap-2">
                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                  <p className="text-sm font-bold text-slate-800">Alert sent.</p>
                  <p className="text-xs text-slate-400">Your dispatcher and company have been notified.</p>
                </div>
              ) : (
                <>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5 block">
                    What's happening? (optional, but helps them respond faster)
                  </label>
                  <textarea
                    rows={3}
                    value={sosNote}
                    onChange={(e) => setSosNote(e.target.value)}
                    placeholder="e.g. Vehicle breakdown, medical concern, unsafe conditions…"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-100 resize-none"
                  />
                  <p className="text-[11px] text-slate-400 mt-2">
                    This sends immediately regardless of your communication pause setting. If you're in
                    immediate danger, call 911 first.
                  </p>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => setSosOpen(false)}
                      disabled={sosSending}
                      className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-xs font-bold hover:bg-gray-50 transition disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendSOS}
                      disabled={sosSending}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition disabled:opacity-60 flex items-center justify-center gap-1.5"
                    >
                      {sosSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertOctagon className="w-3.5 h-3.5" />}
                      {sosSending ? "Sending…" : "Send Alert"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showCompletionPrompt && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center text-3xl mx-auto mb-4">✅</div>
            <h3 className="font-bold text-gray-900 text-lg">Job completed!</h3>
            <p className="text-sm text-gray-500 mt-2">
              Generate and send your invoice now while the job details are fresh, or do it later from Invoices.
            </p>
            <div className="flex flex-col gap-2 mt-5">
              <button
                onClick={() => navigate("/invoices")}
                className="w-full text-white py-2.5 rounded-xl text-sm font-bold transition-colors"
                style={{ background: "#1a2744" }}
              >
                📄 Create Invoice Now
              </button>
              <button
                onClick={() => navigate("/my-jobs")}
                className="w-full bg-gray-50 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-100 transition-colors"
              >
                I'll do it later
              </button>
            </div>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
        .custom-scrollbar:hover::-webkit-scrollbar-thumb { background-color: #94a3b8; }
      `}} />
    </div>
  );
}